import React, { useRef, useState } from 'react';
import './find-booking.scss';
import { useOSBContentFragment } from '../../../../hooks/owners-osb/aem-content-util/use-osb-content-fragment';
import { FMButton, FMInput } from '@own/fds-react';
import { useHistory } from 'react-router-dom';
import { buildNavigationUrl } from '../../owners-osb/osb-utils';
import { OSBPathRetrieveAccessCodeView } from '../osb-view-controller';

interface InputErrorTypes {
    accessCodeError: boolean;
    emailError: boolean;
}

const FindBooking: React.FC = () => {
    const accessCodeRef = useRef<HTMLInputElement>(null);
    const emailInputRef = useRef<HTMLInputElement>(null);

    const history = useHistory();
    const queryParams = history.location.search;

    const [inputError, setInputError] = useState<InputErrorTypes>({
        accessCodeError: false,
        emailError: false,
    });

    const osbContentData =
        useOSBContentFragment('common/osb-v3', 'find-booking') || {};

    const handleFindBookingButtonClick = async () => {
        const emailInputValue = emailInputRef.current?.value ?? '';
        const accessCodeValue = accessCodeRef.current?.value ?? '';

        handleValidationCheck(
            emailInputValue,
            osbContentData.emailRegex?.toString() ?? '',
            'emailError'
        );
        handleValidationCheck(
            accessCodeValue,
            osbContentData.accessCodeRegex?.toString() ?? '',
            'accessCodeError'
        );
    };

    const handleValidationCheck = (
        inputValue: string,
        regexValue: string | null,
        stateParameter: 'emailError' | 'accessCodeError'
    ) => {
        if (regexValue && !RegExp(regexValue).test(inputValue)) {
            handleSetInputError(stateParameter, true);
        } else {
            handleSetInputError(stateParameter, false);
        }
    };

    const handleSetInputError = (
        errorProperty: keyof InputErrorTypes,
        value: boolean
    ): void => {
        setInputError((prevError: InputErrorTypes) => ({
            ...prevError,
            [errorProperty]: value,
        }));
    };

    return (
        <div className="findBookingWrapper">
            <div className="findBookingTitleWrapper">
                {osbContentData.findYourBookingTitle || ''}
            </div>
            <div className="findBookingFormWrapper">
                <form action="post" className="findBookingForm">
                    <FMInput
                        ariaLabel="email-input"
                        header={
                            String(osbContentData.emailInputPlaceholder) || ''
                        }
                        name="email-input"
                        customClass={`emailInput ${
                            inputError.emailError ? 'emailInputError' : ''
                        }`}
                        type="email"
                        ref={emailInputRef}
                        onChange={() => {
                            handleValidationCheck(
                                emailInputRef.current?.value ?? '',
                                osbContentData.emailRegex?.toString() ?? '',
                                'emailError'
                            );
                        }}
                    />
                    <div className="inputFieldError">
                        {inputError.emailError
                            ? osbContentData?.emailError || ''
                            : ''}
                    </div>
                    <FMInput
                        header={
                            String(osbContentData.accessCodeInputPlaceholder) ||
                            ''
                        }
                        ariaLabel="access-code-input"
                        name="access-code-input"
                        customClass={`accessCodeInput ${
                            inputError.accessCodeError
                                ? 'accessCodeInputError'
                                : ''
                        }`}
                        type="text"
                        ref={accessCodeRef}
                        onChange={() => {
                            handleValidationCheck(
                                accessCodeRef.current?.value ?? '',
                                osbContentData.accessCodeRegex?.toString() ??
                                    '',
                                'accessCodeError'
                            );
                        }}
                    />
                    <div className="accessCodeError inputFieldError">
                        {inputError.accessCodeError
                            ? osbContentData?.accessCodeError || ''
                            : ''}
                    </div>
                    <button
                        type="button"
                        className="recoverAccessCodeButton"
                        onClick={() => {
                            history.push(
                                buildNavigationUrl(
                                    OSBPathRetrieveAccessCodeView(),
                                    queryParams
                                )
                            );
                        }}
                    >
                        {osbContentData.recoverAccessCodeLabel || ''}
                    </button>
                    <FMButton
                        type="primary"
                        label={
                            osbContentData.findBookingButtonLabel ||
                            'Find Booking'
                        }
                        onClick={handleFindBookingButtonClick}
                    />
                </form>
            </div>
        </div>
    );
};

export default FindBooking;
